<template>
    <page-loading v-if="loading"></page-loading>
    <div v-else-if="!success || campaign?.flow_id">
        <step-form-heading v-if="!props.campaignId" :icon="faFlagSwallowtail">
            New Campaign

            <template v-slot:description>
                Create one-time, scheduled, and evergreen campaigns.
            </template>
        </step-form-heading>

        <campaign-details :campaignId="props.campaignId">
            <form-wrapper
                :sections="[
                    { id: 'name', label: 'Name' },
                    { id: 'category', label: 'Category' },
                    { id: 'audience_type', label: 'Audience' },
                    { id: 'when', label: 'When' },
                    { id: 'what', label: 'What' },
                    { id: 'to', label: 'To' },
                ]"
                :hideNav="success || !!props.campaignId"
            >
                <div v-if="campaign?.flow_id">
                    <flow-resource-state-alert :resourceName="campaign?.name"
                        :success="success"
                        :editing="!!campaign?.id"
                        :flowId="campaign?.flow_id"
                        :flowSection="campaign?.flow_section">
                    </flow-resource-state-alert>
                </div>

                <template v-if="!success || props.campaignId">
                    <campaign-step-form-name id="name"
                        v-model="campaign.name"
                        :error="errors.name || displayFirstError(validation.name.$errors)"></campaign-step-form-name>
                    <resource-category
                        id="category"
                        v-model="campaign.category"
                        :disabled="!!campaign.flow_id">
                    </resource-category>
                    <campaign-step-form-audience-type
                        id="audience_type"
                        :disabled="disabled"
                        v-model="campaign.type"
                        :campaignSegments="campaign?.segments || []"></campaign-step-form-audience-type>
                    <campaign-step-form-when
                        id="when"
                        ref="whenStepRef"
                        :disabled="disabled"
                        :campaignStatus="campaign.status"
                        :campaignStartedAt="campaign.started_at"
                        :originalLiveAt="campaign.original_live_at"
                        v-model:isRepeatable="campaign.is_repeatable"
                        v-model:durationType="campaign.duration_type"
                        v-model:liveAt="campaign.live_at"
                        v-model:completeAt="campaign.complete_at"
                        v-model:timeZoneAware="campaign.time_zone_aware"></campaign-step-form-when>
                    <campaign-step-form-what
                        id="what"
                        :campaignId="campaign.id"
                        v-model="campaign.campaign_messages"></campaign-step-form-what>
                    <campaign-step-form-to
                        id="to"
                        :disabled="disabled"
                        :campaignId="campaign.id"
                        :campaignType="campaign.type"
                        :campaignDurationType="campaign.duration_type"
                        :campaignStatus="campaign.status"
                        :flowId="campaign?.flow_id"
                        v-model:originalCampaignId="campaign.original_campaign_id"
                        v-model:segmentIds="campaign.segments"
                        v-model:externalContactListIds="campaign.external_contact_lists"
                        v-model:everyone="campaign.everyone"
                        v-model:removeOnResponse="campaign.remove_on_response"
                        v-model:removeOnLinkClick="campaign.remove_on_link_click"
                        v-model:isRepeatable="campaign.is_repeatable"
                        v-model:repeatDelay="campaign.repeat_delay"
                        v-model:audiencePercentage="campaign.audience_percentage"></campaign-step-form-to>

                        <vx-alert v-if="error"
                            color="danger"
                            :closeable="false">
                            {{ error }}
                        </vx-alert>

                        <vx-sticky-footer>
                            <vx-button href="/messaging/campaigns"
                                class="vx-w-20"
                                size="lg"
                                color="muted">
                                Cancel
                            </vx-button>
                            <vx-button @click="preSubmit()"
                                data-test="submit-button"
                                class="vx-grow"
                                size="lg"
                                color="primary"
                                :loading="submiting">
                                {{
                                    props.campaignId
                                        ? "Update Campaign"
                                        : "Create Campaign"
                                }}
                        </vx-button>
                    </vx-sticky-footer>
                </template>
            </form-wrapper>
        </campaign-details>
    </div>

    <template v-else>
        <success-box>
            Your campaign has been {{ props.campaignId ? 'updated' : 'created' }}!

            <template v-slot:action>
                <vx-button size="lg"
                    href="/messaging/campaigns"
                    color="secondary">
                    View All Campaigns
                    <font-awesome-icon :icon="faArrowRight"></font-awesome-icon>
                </vx-button>
            </template>
        </success-box>
    </template>
</template>

<script setup>
import { VxAlert, VxButton, VxStickyFooter, VxSwal } from '@voxie/frontend-components';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import SuccessBox from '~/components/general/SuccessBox.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowRight, faFlagSwallowtail } from '@fortawesome/pro-solid-svg-icons';
import { computed, inject, onMounted, ref } from 'vue';
import CampaignStepFormName from './CampaignStepFormName.vue';
import CampaignStepFormAudienceType from './CampaignStepFormAudienceType.vue';
import CampaignStepFormWhen from './CampaignStepFormWhen.vue';
import CampaignStepFormWhat from './CampaignStepFormWhat.vue';
import CampaignStepFormTo from './CampaignStepFormTo.vue';
import { useUrlSearchParams } from '@vueuse/core';
import campaignsClient from '~/services/campaignsClient';
import FlowResourceStateAlert from '~/components/automation/flows/components/FlowResourceStateAlert.vue';
import { useToasted } from '~/composables/useToasted';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, statuses as CampaignStatus } from '~/constants/campaign';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { formatTz, getClientTz, getTeamTz } from '~/utils/date';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';
import flowsClient from '~/services/flowsClient';
import { clientTzToAmericaNewYorkUTC, UTCNewYorkToClientTz } from './timeZoneUtils';
import ResourceCategory from '~/components/general/categories/ResourceCategory.vue';
import CampaignDetails from '../CampaignDetails.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import PageLoading from '~/components/general/PageLoading.vue';

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    campaignId: {
        type: Number,
        required: false,
    },
});

const FEATURES = inject('FEATURES');

const whenStepRef = ref();

const error = ref();
const errors = ref({});
const success = ref(false);
const submiting = ref(false);
const loading = ref(true);

const campaign = ref({
    name: '',
    type: FEATURES.transactional_default ? AUDIENCE_TYPE_TRANSACTIONAL : AUDIENCE_TYPE_MARKETING,
    duration_type: '',
    live_at: new Date(),
    original_live_at: toUTCDateTimeFormat(dayjs()),
    complete_at: null,
    original_campaign_id: null,
    segments: [],
    external_contact_lists: [],
    everyone: false,
    remove_on_response: false,
    remove_on_link_click: false,
    time_zone_aware: false,
    is_repeatable: false,
    repeat_delay: 10080,
    audience_percentage: null,
    campaign_messages: [],
    category: '',
});

const validation = useVuelidate({
    name: { required, maxLength: maxLength(180), $autoDirty: true },
}, campaign)

const queryParams = useUrlSearchParams();

const disabled = computed(() => props.campaignId && ![CampaignStatus.DRAFT, CampaignStatus.SCHEDULED].includes(campaign.value.status));
const safeHoursTz = computed(() => campaign.value.time_zone_aware ? getClientTz() : getTeamTz());

const transformResponse = (data) => {
    const transformed = {
        ...data,
    }
    if (data.time_zone_aware) {
        transformed.live_at = UTCNewYorkToClientTz(data.live_at);
        transformed.complete_at = UTCNewYorkToClientTz(data.complete_at);
    } else {
        transformed.live_at = data.live_at ? dayjs.utc(data.live_at).toDate() : null;
        transformed.complete_at = data.complete_at ? dayjs.utc(data.complete_at).toDate() : null;
    }

    transformed.original_live_at = data.live_at;
    transformed.original_complete_at = data.complete_at;

    return transformed;
}

const duplicate = async () => {

    const response = await campaignsClient.campaignGet(teamId, queryParams.duplicate_from_id);

    let flowSection;
    if (response.data.flow_id) {
        try {
            const { data: { config: { sections } } } = await flowsClient.getConfig(teamId, response.data.flow_id);
            flowSection = sections.findIndex(
                (section) => section.resources.find(
                    resource => resource.type === 'campaign' && Number(resource.id) === Number(queryParams.duplicate_from_id)
                )
            )
        } catch (e) {
            console.error(e)
        }
    }

    campaign.value = {
        ...transformResponse(response.data),
        id: undefined,
        name: '',
        flow_section: flowSection === -1 ? 0 : flowSection,
    }
}

onMounted(async () => {
    try {
        loading.value = true;
        if (props.campaignId) {
            const response = await campaignsClient.campaignGet(teamId, props.campaignId);
            campaign.value = transformResponse(response.data);
        }

        if (queryParams.duplicate_from_id) {
            await duplicate();
        }

        if (!campaign.value.flow_id && queryParams.flow_id) {
            campaign.value.flow_id = queryParams.flow_id;
            campaign.value.flow_section = Number(queryParams.flow_section || 0);
        }
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
});

const submit = async () => {
    try {
        error.value = '';
        errors.value = {}
        submiting.value = true;

        const data = { ...campaign.value };

        if (data.time_zone_aware) {
            // when time_zone_aware is set to true we send to the server as if the date was set in America/New_York
            data.live_at = clientTzToAmericaNewYorkUTC(data.live_at);
            data.complete_at = clientTzToAmericaNewYorkUTC(data.complete_at);
        } else {
            data.live_at = data.live_at ? toUTCDateTimeFormat(dayjs.tz(data.live_at, getClientTz())) : null;
            data.complete_at = data.complete_at ? toUTCDateTimeFormat(dayjs.tz(data.complete_at, getClientTz())) : null;
        }

        data.segment_ids = data.segments.map(segment => segment.id);
        data.external_contact_list_ids = data.external_contact_lists.map(externalContactList => externalContactList.id);

        if (disabled.value) {
            delete data.segment_ids;
            delete data.external_contact_list_ids;
        }

        delete data.original_live_at;
        delete data.original_complete_at;

        await campaignsClient.campaignSave(teamId, data);

        success.value = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
        console.error(e)
        if (e?.response?.status === HTTP_UNPROCESSABLE_ENTITY) {
            errors.value = e.response.data.errors;
        }

        if (e?.response?.status === HTTP_INTERNAL_SERVER_ERROR) {
            error.value = 'An unexpected error occurred. Please try again.';
        } else {
            error.value = e.response?.data?.message;
        }

        toasted.error(error.value || 'Something went wrong.');
    } finally {
        submiting.value = false;
    }
};

const preSubmit = async () => {
    if (submiting.value) {
        return;
    }

    await whenStepRef.value.validate();
    const isValid = await validation.value.$validate();

    if (!isValid) {
        error.value = displayFirstError(validation.value) || 'Please check form data.';
        toasted.error(displayFirstError(validation.value) || 'Please check form data.');
        return;
    }

    const startTime = getStartAtOutsideSafeHours();

    if (campaign.value.duration_type === 'one_time' && startTime !== null) {
        const formattedTimeZone = campaign.value.time_zone_aware
            ? 'local time'
            : formatTz(startTime.format('z'));

        const formattedStartTime = startTime.format('h:mm a');

        const html = `<div class="vx-flex vx-flex-col vx-gap-3">
                <span>Your campaign is scheduled to start at <strong>${formattedStartTime} ${formattedTimeZone}</strong>, which could send messages outside of the safe sending hours of 8am to 9pm.</span>
                <span>This could lead to regulatory issues and increased opt-outs. We recommend scheduling between 10 am to 6 pm to drive the most engagement.</span>
            </div>`;

        await VxSwal.fire({
            icon: 'danger',
            title: 'Outside Safe Hours!',
            html: html,
            showCancelButton: true,
            confirmButtonColor: 'danger',
            confirmButtonText: 'Proceed anyway',
            preConfirm: () => submit(),
        });
    } else {
        await submit();
    }
};

const isLiveAtOutsideSafeHours = () => {
    const liveAt = dayjs(campaign.value.live_at).tz(safeHoursTz.value);

    return !liveAt.isBetween(
        liveAt.startOf('day').hour(8),
        liveAt.startOf('day').hour(20),
        'hour',
        '[)' // Inclusive start, exclusive end
    );
};

const getStartAtOutsideSafeHours = () => {
    if (!campaign.value.live_at) {
        return null;
    }

    if (isLiveAtOutsideSafeHours()) {
        return dayjs(campaign.value.live_at).tz(safeHoursTz.value);
    }

    const campaignMessage = campaign.value.campaign_messages.find((message) => {
        if (message.should_send_on_schedule) {
            const startDate = dayjs().tz(safeHoursTz.value).startOf('day');
            const messageStartAt = getMessageStartAt(message.send_at_time);

            return !messageStartAt.isBetween(
                startDate.hour(8),
                startDate.hour(20),
                'hour',
                '[)' // Inclusive start, exclusive end
            );
        }
    });

    return campaignMessage ? getMessageStartAt(campaignMessage.send_at_time) : null;
};

const getMessageStartAt = (sendAtTime) => {
    const startDate = dayjs().tz(safeHoursTz.value).startOf('day');
    const [hours, minutes] = sendAtTime.split(':').map(Number);

    return startDate.hour(hours).minute(minutes);
};

</script>
